import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  X as XIcon,
} from "lucide-react";
import "./Toast.css";

const TOAST_EVENT = "vs-toast";
const DEFAULT_DURATION = 4000;

let nextId = 1;

function emit(variant, message, opts = {}) {
  if (typeof window === "undefined") return;
  const detail = {
    id: nextId++,
    variant,
    message,
    duration: opts.duration ?? DEFAULT_DURATION,
  };
  window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail }));
  return detail.id;
}

export const toast = {
  success: (msg, opts) => emit("success", msg, opts),
  warning: (msg, opts) => emit("warning", msg, opts),
  danger: (msg, opts) => emit("danger", msg, opts),
  error: (msg, opts) => emit("danger", msg, opts),
  info: (msg, opts) => emit("info", msg, opts),
  show: (msg, opts) => emit(opts?.variant || "info", msg, opts),
};

const ICONS = {
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
  info: Info,
};

export default function Toast() {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  useEffect(() => {
    const onToast = (e) => {
      const t = e.detail;
      if (!t || !t.message) return;
      setToasts((prev) => [...prev, t]);
      const timer = setTimeout(() => {
        setToasts((prev) => prev.filter((x) => x.id !== t.id));
        timersRef.current.delete(t.id);
      }, t.duration);
      timersRef.current.set(t.id, timer);
    };
    window.addEventListener(TOAST_EVENT, onToast);
    const timers = timersRef.current;
    return () => {
      window.removeEventListener(TOAST_EVENT, onToast);
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const dismiss = (id) => {
    setToasts((prev) => prev.filter((x) => x.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="vs-toast__stack"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      {toasts.map((t) => {
        const Icon = ICONS[t.variant] || Info;
        return (
          <div
            key={t.id}
            className={`vs-toast vs-toast--${t.variant}`}
            role={t.variant === "danger" ? "alert" : "status"}
          >
            <span className="vs-toast__icon" aria-hidden="true">
              <Icon size={18} strokeWidth={2} />
            </span>
            <span className="vs-toast__message">{t.message}</span>
            <button
              type="button"
              className="vs-toast__close"
              aria-label="Dismiss notification"
              onClick={() => dismiss(t.id)}
            >
              <XIcon size={14} />
            </button>
          </div>
        );
      })}
    </div>,
    document.body
  );
}
